import axios from 'axios'
import { useEffect, useState } from 'react'
import IconMail from '../../component/icon/IconMail'
import IconLockDots from '../../component/icon/IconLockDots'
import { Link, useNavigate } from 'react-router-dom'
import BlankLayout from '../../component/layout/BlankLayout'
import { languageList } from '../../util/helper'
import Dropdown from '../../component/Dropdown'
import i18next from 'i18next'
import IconGlobe from '../../component/icon/IconGlobe'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Helmet } from 'react-helmet'


const ResetPassword = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [showResetPassword, setShowResetPassword] = useState(false)
    const [loading, setLoading] = useState(false)
    const [timeLeft, setTimeLeft] = useState(0)

    // Function to start the timer and store timestamp
    const startTimer = () => {
        const now = Date.now()
        const expireTime = now + 60 * 1000 // 1-minute expiry
        localStorage.setItem("otp_timer", expireTime.toString())
        setTimeLeft(60)
    }

    // Effect to check the timer on mount
    useEffect(() => {
        const storedExpireTime = localStorage.getItem("otp_timer")
        if (storedExpireTime) {
            const remainingTime = Math.floor((parseInt(storedExpireTime) - Date.now()) / 1000)
            if (remainingTime > 0) {
                setTimeLeft(remainingTime)
            }
        }

        // Timer countdown
        const interval = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(interval)
                    localStorage.removeItem("otp_timer")
                    return 0
                }
                return prev - 1
            });
        }, 1000)

        return () => clearInterval(interval)
    }, [])

    const generateOtp = async (values:any) => {
        if (!values.email || values.email.trim() === "") return toast.error(t('register.email_required'))

        setLoading(true)
        axios.post(`${process.env.REACT_APP_API_BASE_URL}/user/generate-otp`, {
            "email": values.email.trim(),
            "reset_password": true,
        }).then( res => {
            setLoading(false)
            console.log("res", res)
            if (res.status == 200) {
                setShowResetPassword(true)
            }
            startTimer() // Start timer after OTP request
        }).catch( err => {
            setLoading(false)
            console.log("err", err.response)
            if (err.response.data && err.response.data.message) {
                toast.error(err.response.data.message)
            } else {
                toast.error(t("server_error"))
            }
        })
    }

    const resetPassword = async (values:any) => {
        if (values.email.trim() === "" || values.otp.trim() === "") return toast.error(t('register.email_otp_required'))

        setLoading(true)
        axios.post(`${process.env.REACT_APP_API_BASE_URL}/user/reset-password`, {
            "email": values.email.trim(),
            "otp": values.otp.trim(),
            "new_password": values.password,
            "confirm_new_password": values.confirmPassword,
        }).then( res => {
            setLoading(false)
            console.log("res", res)
            alert("Password reset succesfully")
            navigate("/login")

        }).catch( err => {
            setLoading(false)
            console.log("err", err.response)
            alert(err.response.data ? err.response.data.error : "Error Resetting")
        })
    }
    
    const validationSchema = (showResetPassword: boolean) => 
        Yup.object().shape({
            email: Yup.string()
                .required(t("validation.required"))
                .test(
                    'no-trim-spaces',
                    t("validation.no_spaces_allowed"),
                    value => value === value?.trim()
                )
                .email(t("validation.invalid_email")),
    
            otp: showResetPassword 
                ? Yup.string().required(t("validation.required"))
                : Yup.string(),
    
            password: showResetPassword
                ? Yup.string().min(6, t("validation.password_min")).required(t("validation.required"))
                : Yup.string(),
    
            confirmPassword: showResetPassword
                ? Yup.string()
                    .oneOf([Yup.ref("password")], t("validation.password_match"))
                    .required(t("validation.required"))
                : Yup.string(),
        })

    return (
        <>
        <Helmet>
            <title>Sign Up as Coach | MyKoku Platform Malaysia</title>
            <meta name="description" content="Create your free coach account on MyKoku. Manage students, track attendance, and schedule lessons – perfect for Malaysian tutors and coaching centers."/>
        </Helmet>
        <BlankLayout>
            <div className="relative flex min-h-screen items-center justify-center bg-cover bg-center bg-no-repeat px-6 py-10 dark:bg-[#060818] sm:px-16">
                <div className="relative w-full max-w-[870px] rounded-md bg-[linear-gradient(45deg,#fff9f9_0%,rgba(255,255,255,0)_25%,rgba(255,255,255,0)_75%,_#fff9f9_100%)] p-2 dark:bg-[linear-gradient(52.22deg,#0E1726_0%,rgba(14,23,38,0)_18.66%,rgba(14,23,38,0)_51.04%,rgba(14,23,38,0)_80.07%,#0E1726_100%)]">
                    <div className="relative flex flex-col justify-center rounded-md backdrop-blur-lg dark:bg-black/50 px-6 lg:min-h-[758px] py-20">
                        <div className="mx-auto w-full max-w-[440px]">
                            <div className="mb-10">
                                <img className="w-20" src="/assets/images/koku.png" alt="logo"/>
                                <h1 className="text-3xl font-extrabold uppercase !leading-snug text-primary md:text-4xl">{t('forgot_password.title')}</h1>
                                <p className="text-base font-bold leading-normal text-white-dark">{t('forgot_password.subtitle')}</p>
                            </div>
                            <Formik
                                initialValues={{ email: "", otp: "", password: "", confirmPassword: "" }}
                                validationSchema={validationSchema(showResetPassword)}
                                onSubmit={(values) => {
                                    showResetPassword ? resetPassword(values) : generateOtp(values)
                                }}
                            >
                                {({ isSubmitting }) => (
                                <Form>
                                    <div className="space-y-5 dark:text-white">
                                        <div>
                                            <label className="text-white-dark">{t('forgot_password.email')}</label>
                                            <Field type="email" name="email" placeholder={t("forgot_password.enter_email")} className="form-input placeholder:text-white-dark" />
                                            <ErrorMessage name="email" component="div" className="mt-1 text-red-500 text-sm" />
                                        </div>
                                        {showResetPassword && (
                                        <>
                                        <div>
                                            <label className="text-white-dark">{t('forgot_password.otp')}</label>
                                            <Field type="text" name="otp" placeholder={t("forgot_password.enter_otp")} className="form-input placeholder:text-white-dark" />
                                            <ErrorMessage name="otp" component="div" className="mt-1 text-red-500 text-sm" />
                                        </div>
                                        <div>
                                            <label className="text-white-dark">{t('forgot_password.password')}</label>
                                            <Field type="password" name="password" placeholder={t("forgot_password.enter_password")} className="form-input placeholder:text-white-dark" />
                                            <ErrorMessage name="password" component="div" className="mt-1 text-red-500 text-sm" />
                                        </div>
                                        <div>
                                            <label className="text-white-dark">{t('forgot_password.confirm_password')}</label>
                                            <Field type="password" name="confirmPassword" placeholder={t("forgot_password.enter_confirm_password")} className="form-input placeholder:text-white-dark" />
                                            <ErrorMessage name="confirmPassword" component="div" className="mt-1 text-red-500 text-sm" />
                                        </div>
                                        </>
                                        )}
                                        <button type="submit" disabled={(!showResetPassword && loading) || (!showResetPassword && timeLeft > 0)} className="btn btn-primary rounded-3xl !mt-6 w-full border-0 uppercase shadow-[0_10px_20px_-10px_rgba(67,97,238,0.44)]">
                                            {loading ? (
                                                <span className="animate-spin border-2 border-black dark:border-white !border-l-transparent rounded-full w-5 h-5 inline-flex"></span>
                                            ) : (
                                                showResetPassword ? t('forgot_password.reset') : timeLeft > 0 ? `Resend OTP (${timeLeft}s)` : t("forgot_password.send_otp")
                                            )}
                                        </button>
                                    </div>
                                </Form>
                                )}
                            </Formik>
                            <div className="relative my-7 text-center md:mb-9">
                                <span className="absolute inset-x-0 top-1/2 h-px w-full -translate-y-1/2 bg-white-light dark:bg-white-dark"></span>
                                <span className="relative bg-white px-2 font-bold uppercase text-white-dark dark:bg-dark dark:text-white-light">or</span>
                            </div>
                            <div className="text-center text-white-dark dark:text-white">
                                {t('forgot_password.already_have_account')}&nbsp;
                                <Link to="/login" className="uppercase text-primary underline transition hover:text-black dark:hover:text-white">
                                {t('forgot_password.sign_in')}
                                </Link>
                            </div>

                            <div>
                                <Dropdown placement={`bottom-start`} btnClassName=" p-2 rounded-full bg-white-light/40 dark:bg-dark/40 hover:text-primary hover:bg-white-light/90 dark:hover:bg-dark/60" button={<IconGlobe/>} >
                                    <ul className="p-2 text-dark dark:text-white-dark grid grid-cols-2 gap-2 font-semibold dark:text-white-light/90 w-[280px]">
                                        {languageList.map((item: any) => {
                                            return (
                                                <li key={item.code}>
                                                    <button
                                                        type="button"
                                                        className={`flex pt-2 w-full hover:text-primary rounded-lg ${i18next.language === item.code ? 'bg-primary/10 text-primary' : ''}`}
                                                        onClick={() => {
                                                            i18next.changeLanguage(item.code)
                                                            // setFlag(item.code);
                                                        }}
                                                    >
                                                        <img src={`/assets/images/flags/${item.code.toUpperCase()}.svg`} alt="flag" className="w-5 h-5 object-cover rounded-full" />
                                                        <span className="ltr:ml-3 rtl:mr-3">{item.name}</span>
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </Dropdown>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </BlankLayout>
        </>
    )
}

export default ResetPassword